import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, ShoppingBag, Heart, Share2, Store as StoreIcon, Play, ZoomIn, BadgeCheck, Package, Clock, Truck, Camera, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import ProductImage from "@/components/ProductImage";

export default function ProductDetail() {
    const { product_id } = useParams();
    const { t, lang } = useLang();
    const { user } = useAuth();
    const { add } = useCart();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [qty, setQty] = useState(1);
    const [showVideo, setShowVideo] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [isFav, setIsFav] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewForm, setReviewForm] = useState({ rating: 5, delivery_rating: 5, packaging_rating: 5, seller_rating: 5, comment: "", images: [] });

    const load = () => api.get(`/products/${product_id}`).then((r) => { setData(r.data); setIsFav(!!r.data.is_favorite); });
    useEffect(() => { load(); }, [product_id]);

    if (!data) return <Layout><div className="min-h-[60vh] grid place-items-center text-muted-foreground">...</div></Layout>;

    const { product, store, reviews, similar, frequently_bought_together } = data;
    const price = product.discount_price || product.price;
    const hasDiscount = product.discount_price && product.discount_price < product.price;

    const onAdd = async () => {
        if (!user) { toast.error(t("loginRequired")); return; }
        await add(product.product_id, qty);
        toast.success(t("addToCart"));
    };

    const toggleFav = async () => {
        if (!user) { toast.error(t("loginRequired")); return; }
        try {
            if (isFav) {
                await api.delete(`/favorites/product/${product.product_id}`);
                setIsFav(false);
            } else {
                await api.post("/favorites", { kind: "product", target_id: product.product_id });
                setIsFav(true);
                toast.success(t("addToWishlist"));
            }
        } catch { toast.error("Error"); }
    };

    const share = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try { await navigator.share({ title: product.title, url }); return; } catch {}
        }
        try { await navigator.clipboard.writeText(url); toast.success("✓"); } catch {}
    };

    const buyBundle = async (extraIds) => {
        if (!user) { toast.error(t("loginRequired")); return; }
        for (const pid of [product.product_id, ...extraIds]) {
            await add(pid, 1);
        }
        toast.success(t("addToCart"));
    };

    const uploadReviewImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/uploads/image", fd);
            setReviewForm((f) => ({ ...f, images: [...f.images, data.url] }));
        } catch { toast.error("Upload failed"); }
    };

    const submitReview = async (e) => {
        e.preventDefault();
        try {
            await api.post("/reviews", { product_id: product.product_id, ...reviewForm });
            toast.success("✓");
            setReviewOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error");
        }
    };

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                <div className="grid md:grid-cols-2 gap-10">
                    {/* Gallery */}
                    <div>
                        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
                            <div className="relative aspect-square bg-surface rounded-3xl overflow-hidden border border-border group">
                                {showVideo && product.video_url ? (
                                    <video src={product.video_url} controls autoPlay className="w-full h-full object-cover" data-testid="product-video" />
                                ) : (
                                    <>
                                        <ProductImage
                                            src={product.images?.[activeImage]}
                                            alt={product.title}
                                            imageClassName="w-full h-full object-cover"
                                        />
                                        {product.images?.[activeImage] && (
                                            <DialogTrigger asChild>
                                                <button
                                                    className="absolute bottom-3 end-3 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
                                                    data-testid="zoom-btn"
                                                    aria-label={t("zoomImage")}
                                                >
                                                    <ZoomIn className="w-4 h-4" />
                                                </button>
                                            </DialogTrigger>
                                        )}
                                    </>
                                )}
                            </div>
                            <DialogContent className="max-w-4xl">
                                <div className="w-full min-h-[320px] max-h-[85vh] overflow-hidden rounded-lg bg-surface">
                                    <ProductImage
                                        src={product.images?.[activeImage]}
                                        alt={product.title}
                                        imageClassName="w-full h-auto max-h-[85vh] object-contain rounded-lg"
                                    />
                                </div>
                            </DialogContent>
                        </Dialog>

                        {(product.images?.length > 1 || product.video_url) && (
                            <div className="mt-4 grid grid-cols-6 gap-2">
                                {product.video_url && (
                                    <button
                                        onClick={() => { setShowVideo(true); }}
                                        className={`relative aspect-square rounded-xl overflow-hidden border-2 ${showVideo ? "border-terracotta" : "border-border"} bg-espresso grid place-items-center`}
                                        data-testid="video-thumb"
                                    >
                                        <Play className="w-5 h-5 text-white fill-white" />
                                    </button>
                                )}
                                {product.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => { setActiveImage(i); setShowVideo(false); }}
                                        className={`aspect-square rounded-xl overflow-hidden border-2 ${i === activeImage && !showVideo ? "border-terracotta" : "border-border"}`}
                                    >
                                        <ProductImage
                                            src={img}
                                            alt={product.title}
                                            imageClassName="w-full h-full object-cover"
                                            showLabel={false}
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div>
                        {store && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <Link to={`/store/${store.slug}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-terracotta">
                                    <StoreIcon className="w-4 h-4" />
                                    <span className="font-bold">{store.name}</span>
                                </Link>
                                {store.verified && (
                                    <span className="inline-flex items-center gap-1 bg-terracotta/10 text-terracotta text-[10px] font-bold px-2 py-0.5 rounded-full">
                                        <BadgeCheck className="w-3 h-3" />
                                        {t("verified")}
                                    </span>
                                )}
                                <button
                                    onClick={async () => {
                                        if (!user) { toast.error(t("loginRequired")); navigate("/login"); return; }
                                        try {
                                            const { data: conv } = await api.post("/chat/conversations", { store_id: store.store_id });
                                            navigate(`/chat?c=${conv.conversation_id}`);
                                        } catch { toast.error("Error"); }
                                    }}
                                    className="ms-auto inline-flex items-center gap-1 text-xs font-bold text-terracotta hover:underline"
                                    data-testid="product-contact-seller-btn"
                                >
                                    <MessageCircle className="w-3.5 h-3.5" />
                                    {t("contactSellerChat")}
                                </button>
                            </div>
                        )}
                        <h1 className="text-3xl md:text-4xl font-black text-espresso mt-3" data-testid="product-title">{product.title}</h1>

                        {product.rating > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-sm">
                                <Star className="w-4 h-4 fill-ochre text-ochre" />
                                <span className="font-bold text-espresso">{product.rating.toFixed(1)}</span>
                                <span className="text-muted-foreground">({reviews.length})</span>
                            </div>
                        )}

                        <div className="mt-6 flex items-end gap-3">
                            <div className="text-4xl font-black text-terracotta" data-testid="product-price">
                                {price.toFixed(2)} <span className="text-lg text-muted-foreground font-medium">{t("SAR")}</span>
                            </div>
                            {hasDiscount && <div className="text-lg line-through text-muted-foreground mb-1">{product.price.toFixed(2)}</div>}
                        </div>

                        {/* Product specs */}
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                            {product.weight && <Spec icon={<Package />} label={t("weight")} value={product.weight} />}
                            {product.prep_time && <Spec icon={<Clock />} label={t("prepTime")} value={product.prep_time} />}
                            {product.estimated_delivery && <Spec icon={<Truck />} label={t("deliveryTime")} value={product.estimated_delivery} />}
                            <Spec icon={<Package />} label={t("available")} value={`${product.stock} ${t("pieces")}`} />
                        </div>

                        <div className="mt-6 flex items-center gap-3 flex-wrap">
                            <div className="flex items-center border border-border rounded-full overflow-hidden">
                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2 text-lg" data-testid="qty-minus">−</button>
                                <span className="px-4 font-bold" data-testid="qty">{qty}</span>
                                <button onClick={() => setQty(qty + 1)} className="px-4 py-2 text-lg" data-testid="qty-plus">+</button>
                            </div>
                            <Button
                                onClick={onAdd}
                                disabled={product.stock === 0}
                                className="flex-1 min-w-[180px] rounded-full h-12 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                                data-testid="add-to-cart-btn"
                            >
                                <ShoppingBag className="w-4 h-4 me-2" />
                                {product.stock === 0 ? t("outOfStock") : t("addToCart")}
                            </Button>
                            <button
                                onClick={toggleFav}
                                className={`p-3 rounded-full border transition-colors ${isFav ? "bg-terracotta text-white border-terracotta" : "bg-white border-border hover:border-terracotta"}`}
                                data-testid="fav-btn"
                                aria-label={t("addToWishlist")}
                            >
                                <Heart className={`w-5 h-5 ${isFav ? "fill-white" : ""}`} />
                            </button>
                            <button
                                onClick={share}
                                className="p-3 rounded-full bg-white border border-border hover:border-terracotta transition-colors"
                                data-testid="share-btn"
                                aria-label={t("share")}
                            >
                                <Share2 className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Details tabs */}
                        <div className="mt-8 border-t border-border pt-6">
                            <Tabs defaultValue="desc">
                                <TabsList>
                                    <TabsTrigger value="desc" data-testid="tab-description">{t("productDescription")}</TabsTrigger>
                                    {product.ingredients && <TabsTrigger value="ing" data-testid="tab-ingredients">{t("ingredients")}</TabsTrigger>}
                                    {product.allergens && <TabsTrigger value="alg" data-testid="tab-allergens">{t("allergens")}</TabsTrigger>}
                                </TabsList>
                                <TabsContent value="desc" className="mt-4 text-sm leading-relaxed text-espresso whitespace-pre-line">
                                    {product.description}
                                </TabsContent>
                                {product.ingredients && <TabsContent value="ing" className="mt-4 text-sm leading-relaxed text-espresso whitespace-pre-line">{product.ingredients}</TabsContent>}
                                {product.allergens && <TabsContent value="alg" className="mt-4 text-sm leading-relaxed text-espresso whitespace-pre-line">{product.allergens}</TabsContent>}
                            </Tabs>
                        </div>
                    </div>
                </div>

                {/* Frequently bought together */}
                {frequently_bought_together?.length > 0 && (
                    <section className="mt-16" data-testid="frequently-bought-section">
                        <h2 className="text-2xl font-black text-espresso mb-4">{t("frequentlyBought")}</h2>
                        <div className="bg-white border border-border rounded-2xl p-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[product, ...frequently_bought_together.slice(0, 2)].map((p, i) => (
                                    <div key={i} className="text-center">
                                        <div className="aspect-square bg-surface rounded-2xl overflow-hidden">
                                            <ProductImage
                                                src={p.images?.[0]}
                                                alt={p.title}
                                                imageClassName="w-full h-full object-cover"
                                                showLabel={false}
                                            />
                                        </div>
                                        <div className="mt-2 font-bold text-sm text-espresso line-clamp-1">{p.title}</div>
                                        <div className="text-terracotta font-black">{(p.discount_price || p.price).toFixed(2)} {t("SAR")}</div>
                                    </div>
                                ))}
                                <div className="grid place-items-center bg-terracotta/10 rounded-2xl p-4">
                                    <div>
                                        <div className="text-xs text-muted-foreground">{t("total")}</div>
                                        <div className="text-lg font-black text-terracotta">
                                            {(product.discount_price || product.price + frequently_bought_together.slice(0, 2).reduce((s, p) => s + (p.discount_price || p.price), 0)).toFixed(0)} {t("SAR")}
                                        </div>
                                        <Button
                                            onClick={() => buyBundle(frequently_bought_together.slice(0, 2).map((p) => p.product_id))}
                                            className="mt-3 rounded-full bg-terracotta text-white text-xs px-4 h-8"
                                            data-testid="add-bundle-btn"
                                        >
                                            {t("addToCart")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Reviews */}
                <section className="mt-16" data-testid="reviews-section">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-black text-espresso">{t("reviewsTitle")} ({reviews.length})</h2>
                        {user && (
                            <Button onClick={() => setReviewOpen(true)} className="rounded-full bg-terracotta text-white" data-testid="write-review-btn">
                                {t("writeReview")}
                            </Button>
                        )}
                    </div>

                    {reviewOpen && (
                        <form onSubmit={submitReview} className="bg-white border border-border rounded-2xl p-6 mb-6 space-y-4" data-testid="review-form">
                            <RatingRow label={t("overallRating")} value={reviewForm.rating} onChange={(v) => setReviewForm({ ...reviewForm, rating: v })} testId="rating-overall" />
                            <RatingRow label={t("deliveryRating")} value={reviewForm.delivery_rating} onChange={(v) => setReviewForm({ ...reviewForm, delivery_rating: v })} testId="rating-delivery" />
                            <RatingRow label={t("packagingRating")} value={reviewForm.packaging_rating} onChange={(v) => setReviewForm({ ...reviewForm, packaging_rating: v })} testId="rating-packaging" />
                            <RatingRow label={t("sellerRating")} value={reviewForm.seller_rating} onChange={(v) => setReviewForm({ ...reviewForm, seller_rating: v })} testId="rating-seller" />
                            <Textarea placeholder={t("yourComment")} rows={3} value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} data-testid="review-comment-input" />
                            <div>
                                <label className="inline-flex items-center gap-2 text-sm bg-surface px-4 py-2 rounded-full cursor-pointer">
                                    <Camera className="w-4 h-4" />
                                    {t("addPhoto")}
                                    <input type="file" accept="image/*" onChange={uploadReviewImage} className="hidden" data-testid="review-photo-input" />
                                </label>
                                {reviewForm.images.length > 0 && (
                                    <div className="mt-2 flex gap-2">
                                        {reviewForm.images.map((img, i) => (
                                            <img key={i} src={fileUrl(img)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" className="rounded-full bg-terracotta text-white" data-testid="review-submit-btn">{t("submit")}</Button>
                                <Button type="button" variant="outline" onClick={() => setReviewOpen(false)} className="rounded-full">{t("cancel")}</Button>
                            </div>
                        </form>
                    )}

                    <div className="space-y-3">
                        {reviews.map((r) => (
                            <div key={r.review_id} className="bg-white rounded-2xl p-5 border border-border" data-testid={`review-${r.review_id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="font-bold text-espresso">{r.user_name}</div>
                                    <div className="text-ochre">{"★".repeat(r.rating)}</div>
                                </div>
                                {r.comment && <p className="mt-2 text-sm text-espresso leading-relaxed">{r.comment}</p>}
                                {r.images?.length > 0 && (
                                    <div className="mt-3 flex gap-2 overflow-x-auto scroll-hide">
                                        {r.images.map((img, i) => (
                                            <img key={i} src={fileUrl(img)} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                                        ))}
                                    </div>
                                )}
                                <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                                    {r.delivery_rating && <span>{t("deliveryRating")}: {"★".repeat(r.delivery_rating)}</span>}
                                    {r.packaging_rating && <span>{t("packagingRating")}: {"★".repeat(r.packaging_rating)}</span>}
                                    {r.seller_rating && <span>{t("sellerRating")}: {"★".repeat(r.seller_rating)}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Similar */}
                {similar?.length > 0 && (
                    <section className="mt-16">
                        <h2 className="text-2xl font-black text-espresso mb-4">{t("similar")}</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {similar.map((p) => <ProductCard key={p.product_id} product={p} />)}
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    );
}

function Spec({ icon, label, value }) {
    return (
        <div className="bg-surface rounded-xl p-3 flex items-center gap-3">
            <div className="text-terracotta">{icon}</div>
            <div className="min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
                <div className="text-sm font-bold text-espresso truncate">{value}</div>
            </div>
        </div>
    );
}

function RatingRow({ label, value, onChange, testId }) {
    return (
        <div className="flex items-center justify-between" data-testid={testId}>
            <div className="text-sm font-bold text-espresso">{label}</div>
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => onChange(n)} className="text-2xl leading-none" data-testid={`${testId}-star-${n}`}>
                        <Star className={`w-6 h-6 ${n <= value ? "fill-ochre text-ochre" : "text-muted-foreground/30"}`} />
                    </button>
                ))}
            </div>
        </div>
    );
}
